import React from 'react';
import NextImage from 'next/image';
import { useTheme } from 'styled-components';

import useBreakpoint from 'Common/hooks/useBreakpoint';
import UniBuddy, { UniBuddyPopCardProps } from 'Common/util/uniBuddy';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import IconButton, { IconButtonProps } from 'Elements/Button/Icon';
import { H2 } from 'Elements/Typography';
import { ImageProps } from 'Elements/Image';
import ArrowLink, { ArrowLinkProps } from 'Elements/Link/Arrow';

import Grid, { Col } from 'Components/Grid';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import SectionNavigationIcons, {
    SectionNavigationIconsProps
} from 'Components/SectionNavigation/Icons';
import PageDivider from 'Components/PageDivider';
import Accordion, { AccordionProps } from 'Components/Accordion';
import Video, { VideoProps } from 'Components/Video';

import MakeWavesHeroBanner, { MakeWavesHeroBannerProps } from 'Modules/HeroBanner/MakeWaves';
import InformationGrid, { InformationGridProps } from 'Modules/InformationGrid';
import BrandBanner, { BrandBannerProps } from 'Modules/Banner/Brand';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: MakeWavesHeroBannerProps;
    welcomeSection: {
        heading: string;
        text: React.ReactNode;
        buttons: IconButtonProps[];
    };
    sectionNavigation: SectionNavigationIconsProps;
    informationGridOne: {
        heading: string;
        grid: InformationGridProps;
    };
    lifeSection: {
        heading: string;
        text: React.ReactNode;
        link?: ArrowLinkProps;
        video?: VideoProps;
        image: ImageProps;
    };
    informationGridTwo: {
        heading: string;
        grid: InformationGridProps;
    };
    countries: {
        heading: string;
        text: React.ReactNode;
        link?: ArrowLinkProps;
        accordions: AccordionProps[];
        order?: React.CSSProperties['order'];
    };
    informationGridThree: {
        heading: string;
        grid: InformationGridProps;
    };
    informationGridFour: {
        heading: string;
        grid: InformationGridProps;
    };
    brandBanner: BrandBannerProps;
    uniBuddyPopcard: { hide: boolean } & UniBuddyPopCardProps;
}

const International: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        welcomeSection,
        sectionNavigation,
        informationGridOne,
        lifeSection,
        informationGridTwo,
        countries,
        informationGridThree,
        informationGridFour,
        brandBanner,
        uniBuddyPopcard
    } = props;

    const { colors, breakpoints } = useTheme();

    const isDesktop = useBreakpoint(breakpoints.large.value);

    const parseSectionNavId = (sectionIndex: number) =>
        sectionIndex < sectionNavigation.links.length
            ? sectionNavigation.links[sectionIndex].link.label?.toLowerCase().split(' ').join('-')
            : undefined;

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <MakeWavesHeroBanner {...heroBanner} />
            <Section id="welcome" paddingTop={1}>
                <Grid>
                    <Col m={10} l={6}>
                        <h2>{welcomeSection.heading}</h2>
                        {welcomeSection.text}
                    </Col>
                    <Col m={10} l={[9, 13]}>
                        {welcomeSection.buttons.map((button, index) => (
                            <IconButton key={index} {...button} style={{ marginBottom: '1rem' }} />
                        ))}
                    </Col>
                </Grid>
            </Section>
            <Section id="section-anchor-links-1">
                <SectionNavigationIcons {...sectionNavigation} />
            </Section>
            <Section id={parseSectionNavId(0) || 'not-started'} paddingTop={1} background>
                <Grid>
                    <Col>
                        <H2
                            textAlign="center"
                            color={colors.primary.hex}
                            spacing={{ large: { margin: '0 0 2.5rem 0' } }}
                        >
                            {informationGridOne.heading}
                        </H2>
                    </Col>
                </Grid>
                <InformationGrid {...informationGridOne.grid} />
            </Section>
            <Section id="life-in-southampton" paddingTop={1}>
                <Grid rowGap="1rem">
                    <Col m={10} l={6}>
                        <h2>{lifeSection.heading}</h2>
                        {lifeSection.text}
                        {lifeSection.link && <ArrowLink {...lifeSection.link} />}
                    </Col>
                    <Col m={10} l={6}>
                        {lifeSection.video ? (
                            <Video {...lifeSection.video} />
                        ) : (
                            <NextImage
                                src={lifeSection.image.src}
                                alt={lifeSection.image.alt || ''}
                                width={648}
                                height={400}
                                style={{ maxWidth: '100%', height: 'auto', borderRadius: '0.5rem' }}
                            />
                        )}
                    </Col>
                </Grid>
            </Section>
            <Section id="section-anchor-links-2">
                <SectionNavigationIcons {...sectionNavigation} progress={2} />
            </Section>
            <Section id={parseSectionNavId(1) || 'start-journey'} paddingTop={1}>
                <Grid>
                    <Col>
                        <H2
                            textAlign="center"
                            color={colors.primary.hex}
                            spacing={{ large: { margin: '0 0 2.5rem 0' } }}
                        >
                            {informationGridTwo.heading}
                        </H2>
                    </Col>
                </Grid>
                <InformationGrid {...informationGridTwo.grid} />
            </Section>
            <PageDivider
                wave="xflipped"
                backgroundColor={colors.accents[9].hex}
                marginBottom={0}
                marginTop={0}
            />
            <Section id="countries">
                <Grid>
                    <Col m={10} l={6} order={isDesktop ? countries.order : 'initial'}>
                        <h2>{countries.heading}</h2>
                        {countries.text}
                        {countries.link && <ArrowLink {...countries.link} />}
                    </Col>
                    <Col m={10} l={6}>
                        {countries.accordions.map(accordion => (
                            <Accordion key={accordion.id} {...accordion} />
                        ))}
                    </Col>
                </Grid>
            </Section>
            <Section id="section-anchor-links-3">
                <SectionNavigationIcons {...sectionNavigation} progress={3} />
            </Section>
            <Section id={parseSectionNavId(2) || 'before-arrive'} paddingTop={1}>
                <Grid>
                    <Col>
                        <H2
                            textAlign="center"
                            color={colors.primary.hex}
                            spacing={{ large: { margin: '0 0 2.5rem 0' } }}
                        >
                            {informationGridThree.heading}
                        </H2>
                    </Col>
                </Grid>
                <InformationGrid {...informationGridThree.grid} />
            </Section>
            <Section id="section-anchor-links-4">
                <SectionNavigationIcons {...sectionNavigation} progress={4} />
            </Section>
            <Section id={parseSectionNavId(3) || 'whilst-here'} paddingTop={1}>
                <Grid>
                    <Col>
                        <H2
                            textAlign="center"
                            color={colors.primary.hex}
                            spacing={{ large: { margin: '0 0 2.5rem 0' } }}
                        >
                            {informationGridFour.heading}
                        </H2>
                    </Col>
                </Grid>
                <InformationGrid {...informationGridFour.grid} />
            </Section>
            <BrandBanner {...brandBanner} />
            {!uniBuddyPopcard.hide &&
                UniBuddy({
                    filterValue: uniBuddyPopcard.filterValue ? uniBuddyPopcard.filterValue : 'All'
                })}
        </Main>
    );
};

export default International;
